import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

const ProjectDetailContainer = styled.div`
  padding: 6rem 1.5rem 5rem;
  background: rgb(var(--bg));
  min-height: 100vh;
  max-width: 1180px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 5rem 1.25rem 4rem;
  }

  @media (max-width: 480px) {
    padding: 4.5rem 1rem 3rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2.75rem;
  padding: 2.25rem 1.5rem 1.9rem;
  border-radius: 22px;
  background: rgb(var(--fg) / 0.035);
  color: rgb(var(--fg));
  border: 1px solid rgb(var(--fg) / 0.06);
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 20% 20%, rgba(255, 21, 97, 0.15), transparent 45%),
      radial-gradient(circle at 82% 22%, rgba(255, 21, 97, 0.08), transparent 35%);
    pointer-events: none;
    border-radius: 22px;
  }

  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 480px) {
    margin-bottom: 2rem;
    padding: 1.75rem 1rem 1.4rem;
    border-radius: 18px;
  }
`;

const HeaderTextBlock = styled.div`
  max-width: 900px;
  margin: 0 auto 1.5rem;
`;


const Title = styled.h1`
  font-size: 2.7rem;
  color: rgb(var(--fg) / 0.95);
  margin-bottom: 0.4rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 2.15rem;
  }

  @media (max-width: 480px) {
    font-size: 1.85rem;
  }
`;

const Date = styled.p`
  font-size: 0.9rem;
  color: rgb(var(--gray) / 0.7);
  letter-spacing: 0.02em;
`;

const TagsContainer = styled.div`
  margin-top: 0;
  display: flex;
  justify-content: center;
  gap: 0.65rem;
  flex-wrap: wrap;
  margin-bottom: 0;
`;

const Tag = styled.span`
  background: rgb(var(--fg) / 0.05);
  color: rgb(var(--fg) / 0.9);
  padding: 0.48rem 0.95rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  border: 1px solid rgba(255, 21, 97, 0.3);
  backdrop-filter: blur(4px);
  transition: transform 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background: rgb(var(--fg) / 0.08);
    transform: translateY(-1px);
    box-shadow: 0 10px 20px rgb(0 0 0 / calc(0.3 * var(--shadow-mul)));
  }
`;

const CoverImage = styled.img`
  display: block;
  width: 100%;
  max-width: 860px;
  height: 360px;
  object-fit: cover;
  object-position: center;
  margin: 1.5rem auto 1rem;
  border-radius: 18px;
  border: 1px solid rgb(var(--fg) / 0.08);
  box-shadow: 0 18px 36px rgb(0 0 0 / calc(0.45 * var(--shadow-mul)));
  background: rgb(var(--fg) / 0.04);
  filter: brightness(0.92) saturate(0.95);
  transition: filter 0.35s ease;

  &:hover {
    filter: brightness(1) saturate(1);
  }

  @media (max-width: 768px) {
    height: 260px;
  }

  @media (max-width: 480px) {
    height: 200px;
    max-width: 100%;
  }
`;

const Section = styled.div`
  margin-bottom: 1.75rem;
  padding: 1.6rem 1.45rem;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  border-radius: 20px;
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));

  @media (max-width: 480px) {
    margin-bottom: 1.25rem;
    padding: 1.2rem 1rem;
    border-radius: 16px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.45rem;
  color: #ff1561;
  margin-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 21, 97, 0.25);
  padding-bottom: 0.75rem;
  font-weight: 800;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }

  @media (max-width: 480px) {
    font-size: 1.18rem;
    padding-bottom: 0.6rem;
  }
`;

const Text = styled.p`
  font-size: 1rem;
  line-height: 1.85;
  color: rgb(var(--fg) / 0.82);
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 0.96rem;
  }

  @media (max-width: 480px) {
    font-size: 0.92rem;
    line-height: 1.75;
  }
`;

const List = styled.ul`
  padding-left: 1.35rem;
  list-style-type: disc;
  color: rgb(var(--fg) / 0.82);
  margin: 0.5rem 0 0;

  @media (max-width: 480px) {
    padding-left: 1.1rem;
  }
`;

const ListItem = styled.li`
  margin-bottom: 0.72rem;
  line-height: 1.7;
  padding-left: 0.1rem;

  &::marker {
    color: #ff1561;
  }
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 2rem auto 0;
  padding: 0.9rem 2rem;
  min-width: 220px;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgb(var(--fg) / 0.92);
  background: rgb(var(--fg) / 0.06);
  border: 1px solid rgb(var(--fg) / 0.14);
  border-radius: 999px;
  cursor: pointer;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: transform 0.25s, border-color 0.25s, background 0.25s;
  text-align: center;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.1);
    border-color: rgba(255, 21, 97, 0.5);
  }

  @media (max-width: 768px) {
    padding: 0.85rem 1.5rem;
    font-size: 0.78rem;
  }

  @media (max-width: 480px) {
    width: 100%;
    min-width: 0;
    padding: 0.8rem 1.2rem;
    font-size: 0.75rem;
  }
`;


const SentimentAnalysis = () => {
  const tags = ['Python', 'NLP', 'Sentiment Analysis', 'Data Analysis'];
  const datePublished = '2024-10-07';
  const navigate = useNavigate();

  return (
    <ProjectDetailContainer>
      <Header>
        <HeaderTextBlock>
          <Title>Sentiment Dynamics and Topic Analysis of Greece’s Premier Summer Islands Destinations</Title>
          <Date>Published on: {datePublished}</Date>
        </HeaderTextBlock>

        <CoverImage src="/images/mentionlytics.jpg" alt="Sentiment Analysis" />

        <TagsContainer>
          {tags.map((tag, index) => (
            <Tag key={index}>{tag}</Tag>
          ))}
        </TagsContainer>
      </Header>

      <Section>
        <SectionTitle>Collaborators</SectionTitle>
        <List>
          <ListItem>
            <a href="https://www.linkedin.com/in/efrosini-pagkali/" target="_blank" rel="noopener noreferrer">Efrosini Pagkali</a>
          </ListItem>
          <ListItem>
            <a href="https://www.linkedin.com/in/konstantinos-lamprou-943038213/" target="_blank" rel="noopener noreferrer">Konstantinos Lamprou</a>
          </ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle >Overview</SectionTitle>
        <Text>
          Short-term consulting project in collaboration with{' '}
          <a href="https://www.mentionlytics.com/" target="_blank" rel="noopener noreferrer">
            Mentionlytics
          </a>{' '}
          and{' '}
          <a href="https://bigblue.academy/en" target="_blank" rel="noopener noreferrer">
            Big Blue Data Academy
          </a>. The project focused on analyzing social media posts associated with five major Greek tourism destinations:
        </Text>
        <List>
          <ListItem><strong>Santorini</strong></ListItem>
          <ListItem><strong>Mykonos</strong></ListItem> 
          <ListItem><strong>Rhodes</strong></ListItem> 
          <ListItem><strong>Crete</strong></ListItem> 
          <ListItem><strong>Corfu</strong></ListItem> 
        </List>
        <Text>
          Because the upstream keyword collection was overly broad, the dataset contained significant noise. As a result, the strongest part of the project was the technical workflow itself — designing a clean, end‑to‑end NLP pipeline capable of handling messy social‑media data.
        </Text>
      </Section>

      <Section>
        <SectionTitle>Analytical Framing & Data Constraints</SectionTitle>
        <Text>
          Although the dataset was large, the keyword-based collection method provided by the platform produced
          significant noise — for example, destination names such as "Rhodes" appearing in unrelated contexts.
          This meant that while destination‑level insights remained part of the formal project scope, the most meaningful contribution on my end became the design of a workflow capable of reliably cleaning, normalizing, and structuring highly unstructured social‑media data.
        </Text>
        <Text>
          The value of this project — particularly from my perspective — lies in the end‑to‑end analytical pipeline: defining extraction rules,
          engineering a robust cleaning framework, handling multilingual text, and connecting Named Entity Recognition,
          sentiment scoring, and topic modeling into a coherent analysis flow. This systematic approach mirrors how
          business analytics projects must operate under real‑world constraints where upstream data quality cannot be
          fully controlled.
        </Text>

      </Section>
      
      <Section>
        <SectionTitle>Objectives</SectionTitle>
        <Text>
          The project was framed around three analytical goals:
        </Text>

        <List>
          <ListItem>Analyze sentiment from social media posts related to Greek tourism destinations.</ListItem>
          <ListItem>Identify dominant themes using NLP techniques such as LDA (Latent Dirichlet Allocation).</ListItem>
          <ListItem>Explore user behaviour and preferences through sentiment dynamics.</ListItem>
        </List>
      </Section>
      
      <Section>
        <SectionTitle>Use Cases</SectionTitle>
        <List>
          <ListItem><strong>Tourism Boards:</strong> Understanding public sentiment can help tourism boards identify areas where improvements can be made, such as overcrowding or environmental conservation.</ListItem>
          <ListItem><strong>Travel Agencies:</strong> Insights into which destinations are associated with positive or negative experiences can assist travel agencies in promoting destinations more effectively.</ListItem>
          <ListItem><strong>Local Businesses:</strong> Identifying trending topics, such as "local cuisine" or "luxury accommodations," helps local businesses tailor their offerings to tourist interests.</ListItem>
        </List>
      </Section>

      <Section>
      <SectionTitle>Tools Used</SectionTitle>
      <Text>
        A variety of tools and libraries were employed throughout the project to process, analyze, and visualize the data. These tools helped ensure efficient data handling, accurate sentiment analysis, and insightful visualizations. Below is a detailed overview of the key tools and their roles in the project:
      </Text>
      <List>
        <ListItem>
          <strong>Mentionlytics:</strong> Used the platform’s built‑in trend tools (such as word clouds) to quickly surface irrelevant terms and refine our exclusion rules before deeper analysis.
        </ListItem>
        <ListItem>
          <strong>Python:</strong> Served as the core scripting environment, enabling seamless integration of data extraction, cleaning logic, NLP components, and visualization workflows.
        </ListItem>
        <ListItem>
          <strong>Pandas and NumPy:</strong> Essential for data manipulation, ensuring efficient handling of messy text inputs, intermediate datasets, and final analytical tables.
        </ListItem>
        <ListItem>
          <strong>Matplotlib and Seaborn:</strong> Utilized for building clear, interpretable visualizations that supported exploratory analysis and presentation of results.
        </ListItem>
        <ListItem>
          <strong>Google Cloud Translate API:</strong> Enabled consistent multilingual processing by translating non‑English posts into English prior to sentiment and topic modeling.
        </ListItem>
        <ListItem>
          <strong>spaCy:</strong> Applied for Named Entity Recognition to identify locations, organizations, and other entities relevant to our aspect‑based sentiment analysis.
        </ListItem>
        <ListItem>
          <strong>Gensim:</strong> Used to run Latent Dirichlet Allocation (LDA), allowing us to uncover underlying conversation themes despite the noisy keyword collection.
        </ListItem>
        <ListItem>
          <strong>VADER:</strong> Provided sentiment scoring optimized for short, informal social‑media text.
        </ListItem>
        <ListItem>
          <strong>EmoLex:</strong> Supplemented sentiment analysis by mapping posts to emotion categories, adding nuance to the interpretation of user tone.
        </ListItem>
      </List>
    </Section>

      <Section>
        <SectionTitle>Methodology</SectionTitle>
        <Text>
          Our research methodology involved leveraging data from the Mentionlytics platform, a powerful social media monitoring tool that provides real-time insights into online conversations. The overall process included several key steps aimed at extracting, cleaning, and analyzing the data to produce meaningful insights. The steps are outlined below:
        </Text>
        <List>
          <ListItem>
            <strong>Data Collection: </strong>
            The dataset was collected using keywords that had already been set by the company before the project was assigned to us. Since Mentionlytics operates on a real‑time basis, the platform had been continuously gathering posts from the moment those keywords were created. Our role was to export the accumulated data — and as we worked on the pipeline, new posts continued to stream in. We eventually cut off the collection window at roughly 440,000 posts, forming the final dataset used for analysis.
          </ListItem>

          <ListItem><strong>Element Extraction: </strong> 
            Before diving into the cleaning process, we focused on extracting key elements from the raw text data that might provide useful insights later on. These included hashtags, mentions of users, URLs, and emojis. The goal here was to preserve these elements because they could add value to sentiment and topic analysis, but they might have been lost during the cleaning phase. For instance, emojis often convey emotion and context, while hashtags can indicate the themes or topics of discussion.
          </ListItem>

          <ListItem><strong>Data Cleaning: </strong> 
            The cleaning process involved several steps to prepare the text for analysis. First, we removed remaining special characters, numbers, and irrelevant symbols that did not contribute to the text meaning. We then normalized the unicode text to standardize the format across posts written in different languages or with different character encodings. The most time-consuming part of this step was applying keyword filtering to eliminate irrelevant topics and posts. This required extensive exploratory data analysis (EDA) to identify the correct keywords for exclusion. We had to carefully refine the filters to avoid mistakenly removing posts that were actually relevant, which was particularly challenging in a dataset spanning multiple languages and contexts.
          </ListItem>

          <ListItem><strong>Content Translation: </strong> 
            Given the multilingual nature of the data, we utilized Google's Cloud Translation API to translate all non-English content into English. This step was crucial for ensuring consistency in the analysis, as sentiment analysis and topic modeling tools typically perform better on English text. The translation process allowed us to work with a unified corpus while preserving the original meaning of the posts as accurately as possible.
          </ListItem>

          <ListItem><strong>Aspect-Based Sentiment Analysis: </strong> 
            After preparing the text, we performed aspect-based sentiment analysis using a combination of VADER and spaCy's Named Entity Recognition (NER). VADER, a rule-based tool designed for social media sentiment analysis, provided sentiment scores for each post. Meanwhile, spaCy's NER was used to identify key entities in the text, such as locations, organizations, and people. This allowed us to perform a more granular analysis by associating specific sentiments with the entities mentioned in the posts. Finally, we employed Latent Dirichlet Allocation (LDA) to conduct topic modeling, which helped us identify the dominant themes in the dataset. Each post was assigned a topic, and we explored the sentiment trends within these topics to understand how different themes were perceived by the audience.
          </ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>Results</SectionTitle>
        <Text>
          Because the dataset was collected using broad, single‑word destination keywords, the insights we could extract were directional rather than definitive. Still, the structured workflow surfaced patterns that highlight how visitors discussed different aspects of their experience across platforms.
        </Text>
        <List>
          <ListItem>
            <strong>Food & Local Experiences — consistently positive signals: </strong>
            Posts referring to food, nightlife, and experiential activities tended to carry the strongest positive sentiment. Even after filtering noise, these themes appeared frequently and remained sentiment‑stable across islands.
          </ListItem>
          <ListItem>
            <strong>Accommodation & Weather — highly variable and context‑dependent: </strong>
            Mentions in these categories showed mixed sentiment. Positive posts pointed to comfort and good service, while negative posts were tied to isolated issues (cleanliness, miscommunication, unexpected heat waves or storms).
          </ListItem>
          <ListItem>
            <strong>Transport & Logistics — the clearest negative trend: </strong>
            Transportation‑related posts (flights, ferries, airport queues) showed the most consistent negativity. Even after aggressive cleaning, this remained the most stable cross‑island pain point.
          </ListItem>
          <ListItem>
            <strong>Topic Stability — broad but interpretable themes: </strong>
            LDA surfaced wide themes (food, beach activities, travel logistics, nightlife). While interpretation required care due to keyword noise, the pipeline successfully separated lifestyle‑centric clusters from transportation‑centric ones.
          </ListItem>
        </List>
        <Text>
          These trends were never intended as strategic tourism guidance; they simply demonstrate how a structured NLP pipeline can generate interpretable patterns even from noisy inputs. The key takeaway for me was applying sentiment scoring, NER, and topic modeling in a consistent, disciplined way.
        </Text>
      </Section>

      <Section>
        <SectionTitle>Presentation</SectionTitle>
        <Text>
          You can watch our presentation below. Note that although the slides are in English, the presentation is in Greek.
        </Text>
        <div style={{ margin: '2.5rem 0', display: 'flex', justifyContent: 'center' }}>
          <iframe width="809" height="520" src="https://www.youtube.com/embed/b9suQJhGrlg?si=4Gzygnb16NpMXVdI" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen></iframe>
        </div>
      </Section>

      <Section>
        <SectionTitle>Conclusion</SectionTitle>
        <Text>
          This project ultimately became an exercise in engineering a robust NLP pipeline for messy, multilingual, and noisy social‑media data. Designing the workflow—from extraction and cleaning to NER, sentiment scoring, and topic modeling—strengthened my ability to structure ambiguous text data into usable analytical outputs.
        </Text>
      </Section>

      <Button onClick={() => navigate('/projects')}>Back to Projects</Button>
    </ProjectDetailContainer>
  );
};

// Export metadata for use in the Projects page
export const sentimentAnalysisMetadata = {
  title: 'Sentiment Dynamics and Topic Analysis',
  description: 'An analysis of social media posts related to Greek tourism destinations using NLP techniques.',
  tags: ['Python', 'NLP', 'Sentiment Analysis', 'Data Analysis'],
  languages: ['Python', 'PowerBI', 'GitHub'],
  datePublished: '2024-10-07',
  path: '/projects/mentionlytics-sentiment',
  coverImage: 'images/mentionlytics.jpg'
};

export default SentimentAnalysis;